import { EstimateTreeOperationMethodDto } from "./estimate-tree-operation-method.dto";
import { EstimateTreeOperationDto } from "./estimate-tree-operation.dto";
import { EstimateTreeEquipmentDto } from "./estimate-tree-equipment.dto";
import {StoredImageDto} from "./stored-image.dto";

export interface EstimateTreeDto {
  id: number;
  estimateId: number;
  workItemType: WorkItemType;
  lat: number;
  lng: number;
  images: StoredImageDto[];
  operations: EstimateTreeOperationDto[];
  operationMethods?: EstimateTreeOperationMethodDto[]
  /** The machines on the work item (filled on the estimate / job read model; absent on older snapshots and single-tree reads). */
  equipment?: EstimateTreeEquipmentDto[];
  height?: string;
  width?: string;
  trunkDiameter?: string;
  species?: string;
  notes?: string;
  crewNotes?: string;
  additionalDetails: TreeAdditionalDetails;
  dragDistanceFeet?: number;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateEstimateTreeDto {
  estimateId: number;
  treeId: number;
  lat: number;
  lng: number;
  height?: string;
  width?: string;
  trunkDiameter?: string;
  species?: string;
  notes?: string;
  crewNotes?: string;
  imageIdsToAdd?: number[];
  imageIdsToRemove?: number[];
  additionalDetails?: TreeAdditionalDetails;
  updatedBy: number;
}

export interface AddEstimateTreeDto {
  workItemType: WorkItemType;
  lat: number;
  lng: number;
}

export const WorkItemType = {
  Tree: 'Tree',
  TreeGroup: 'TreeGroup',
  FallenTree: 'FallenTree',
  Stump: 'Stump',
  DebrisPile: 'DebrisPile',
  Misc: 'Misc'
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional const + companion type pattern
export type WorkItemType = typeof WorkItemType[keyof typeof WorkItemType];

// Tree_trunkCount: AC-267 §4b/B12 — '# of Trunks' (Tree) / 'Avg # Trunks per Tree' (TreeGroup), written by Android.
// The rest are Android's Work Plan answers (WorkPlan.kt), one key per answer as the shared catalog
// (GET api/v1/work-plan/catalog) lists them, also edited in SQ-500 library preparation.
// (Misc_descriptiveNote, the old web editor's Misc note, moved to the operations' notes in V83.)
export type TreeAdditionalDetailsKey = 'TreeGroup_treeCount' | 'DebrisPile_length' | 'debrisManagement' | 'Tree_trunkCount'
  | 'removalStrategy' | 'reachMethod' | 'debrisMethod' | 'riggingAmount' | 'remainingStem'
  | 'brushWood' | 'haulWoodDiameter' | 'cleanup' | 'cleanupLocation' | 'cleanupNote'
  | 'loaderMaterial' | 'loaderAccess' | 'workPlanEquipment'

/** What happens to the wood (or brush) left on site; a nested object under woodSpec / brushSpec (WorkPlan.kt MaterialSpec). */
export interface TreeMaterialSpec {
  diameter: number;
  lengthMode: string;
  lengthFt: number;
  lengthIn: number;
  location: string;
  locationNote: string;
}

// Actual equipment used is independent of historical equipment billing/time.
export type TreeAdditionalDetails = {[K in TreeAdditionalDetailsKey]?: string | number}
  & {equipmentUsed?: string[]; woodSpec?: TreeMaterialSpec; brushSpec?: TreeMaterialSpec}
  /** Marks the October 2026 repair left (SQ-630): the tree a stump was split off, and the one whose photos it should take. */
  & {splitFrom?: number; retypedFrom?: string; stumpPhotoFromTree?: number};
